import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";
import { getReverseGeocode } from "./geocoding-api";

/**
 * These are the keys that Google Maps uses for to describe the country and state (or equivalent)
 * ref: https://developers.google.com/maps/documentation/geocoding/requests-geocoding#address-types
 */
const GOOGLE_MAPS_COUNTRY_KEY = "country";
const GOOGLE_MAPS_STATE_KEY = "administrative_area_level_1";

interface ReverseGeocodingParams {
    lat: number;
    lng: number;
}

export interface DefaultPositionGeocodeResult {
    countryISO2?: string;
    stateISO2?: string;
}

export const reverseGeocode = async ({ lat, lng }: ReverseGeocodingParams): Promise<DefaultPositionGeocodeResult> => {
    const response = await getReverseGeocode({ lat, lng });
    const countryISO2 = response.find((item) => item.types.includes(GOOGLE_MAPS_COUNTRY_KEY))?.short_name;
    const stateISO2 = response.find((item) => item.types.includes(GOOGLE_MAPS_STATE_KEY))?.short_name;

    return {
        countryISO2,
        stateISO2,
    };
};

/**
 * Cached default position geocode result
 */
let defaultPositionGeocodePromise: Promise<DefaultPositionGeocodeResult> | null = null;

interface GetDefaultPositionReverseGeocodeParams {
    forceRefresh?: boolean;
}
/**
 * Perform the geocode lookup for the account's default position or get the cached result if already defined
 * ** Note this does NOT await the promise, it returns the promise itself
 *
 * Broadly based on the assumption that the default position will be static for the duration of the account load
 */
export const getDefaultPositionReverseGeocode = (
    args?: GetDefaultPositionReverseGeocodeParams,
): Promise<DefaultPositionGeocodeResult> | null => {
    const { forceRefresh = false } = args ?? {};

    if (!defaultPositionGeocodePromise || forceRefresh) {
        try {
            const [defaultLat, defaultLng] = SessionHandler.defaultPosition;
            defaultPositionGeocodePromise = reverseGeocode({ lat: defaultLat, lng: defaultLng });
        } catch (error) {
            // Reset on failure so a subsequent call can retry
            defaultPositionGeocodePromise = null;
            console.error("Error getting default position reverse geocode", error);
        }
    }

    return defaultPositionGeocodePromise;
};
